import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  getAdvancedFormsByObject,
  saveAdvancedSearchCriteria,
  showCrossClientCloneFormModal,
  hideCrossClientCloneFormModal,
  crossClientClone
} from 'actions';

import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';

import ReactTable from 'components/ReactTable';

import AdvancedSearchPanel from 'components/AdvancedSearch/AdvancedSearchPanel';
import AdvancedSearchActions from 'components/AdvancedSearch/AdvancedSearchActions';
import advancedSearchColumnDefs from 'components/AdvancedSearch/GridColumnDefinitions/advancedSearchColumnDefs';

import CrossClientCloneModal from 'components/Modals/CrossClientCloneModal';

const WAIT_INTERVAL = 500;

class AdvancedSearch extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    settings: PropTypes.shape().isRequired,
    formsCount: PropTypes.number.isRequired,
    gridTotalLimit: PropTypes.number.isRequired,
    isFetchingQueues: PropTypes.bool,
    isGettingAccounts: PropTypes.bool,
    isGettingCustomers: PropTypes.bool,
    isGettingForms: PropTypes.bool,
    forms: PropTypes.arrayOf(PropTypes.shape()),
    initSearchCriteria: PropTypes.shape(),
    initGridCriteria: PropTypes.shape(),
    isShowingClientCloneFormModal: PropTypes.bool,
    crossClientCloneForm: PropTypes.shape(),
  };

  static defaultProps = {
    permissions: null,
    isFetchingQueues: false,
    isGettingAccounts: false,
    isGettingCustomers: false,
    isGettingForms: false,
    forms: [],
    initSearchCriteria: null,
    initGridCriteria: null,
    isShowingClientCloneFormModal: false,
    crossClientCloneForm: null
  };

  constructor(props) {
    super(props);

    const { initGridCriteria, initSearchCriteria, gridTotalLimit } = props;
    const searchCriteria = initSearchCriteria || {
      clientIds: null,
      customerIdentifier: '',
      customerName: null,
      accountNumber: '',
      accountName: '',
      formType: '',
    };

    const gridCriteria = initGridCriteria || {
      columnDefs: advancedSearchColumnDefs(false).concat([]),
      pageIndex: 0,
      sortBy: [],
      filters: [],
      pageCount: 0,
      pageSize: 50,
      itemCount: 0,
      totalLimit: gridTotalLimit,
      selectedItems: [],
      selectedItem: null
    };

    this.state = {
      data: [],
      loading: true,
      loadingTime: 500,
      initialLoaded: false,
      searchCriteria,
      gridCriteria
    };
  }

  componentDidMount() {
    this.sendDataRequest();
  }

  /* eslint-disable react/no-did-update-set-state */
  componentDidUpdate(prevProps) {
    const { initialLoaded } = this.state;
    const { forms, formsCount } = this.props;

    if (forms !== prevProps.forms) {
      this.processData(forms, formsCount);
      this.hideLoadingAnimation();
      if (!initialLoaded) {
        this.setState({ initialLoaded: true });
      }
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    const { gridCriteria, searchCriteria } = this.state;
    dispatch(saveAdvancedSearchCriteria(searchCriteria, gridCriteria));
  }

  getActions = () => {
    const { permissions, settings } = this.props;

    return AdvancedSearchActions({
      permissions,
      settings,
      onCloneClick: this.openCloneFormModal
    });
  };

  openCloneFormModal = (row) => {
    const { dispatch } = this.props;
    dispatch(showCrossClientCloneFormModal(row));
  };

  closeCloneFormModal = () => {
    const { dispatch } = this.props;
    dispatch(hideCrossClientCloneFormModal());
  };

  cloneForm = (destination) => {
    const { dispatch, crossClientCloneForm } = this.props;
    dispatch(crossClientClone(crossClientCloneForm, destination));
  };

  hideLoadingAnimation = () => {
    const { loadingTime } = this.state;
    const { isGettingForms, isGettingAccounts, isGettingCustomers, isFetchingQueues } = this.props;
    const loadingInterval = setInterval(() => {
      if (!isGettingForms && !isGettingAccounts && !isGettingCustomers && !isFetchingQueues) {
        this.setState({ loading: false });
        clearInterval(loadingInterval);
      }
    }, loadingTime);
  };

  processData = (items, itemCount) => {
    const { gridCriteria } = this.state;
    const { gridTotalLimit } = this.props;
    const data = [];
    if (items != null) {
      items.forEach((item) => {
        let formType = (item?.formType == null || item?.formType === '') ? 'N/A' : item?.formType;
        if (item.formTypeString != null) {
          formType = item?.formTypeString;
        }
        let accountIsActive = 'N/A';
        if (item.accountIsActive != null) {
          accountIsActive = item.accountIsActive ? 'Active' : 'Inactive';
        }
        let customerIsActive = 'N/A';
        if (item.customerIsActive != null) {
          customerIsActive = item.customerIsActive ? 'Active' : 'Inactive';
        }

        const datum = {
          itemId: item?.id,
          clientId: item?.clientId,
          clientName: item?.clientName,
          accountId: item?.accountId,
          customerDbId: item?.customerDbId,
          // accounts
          accountName: (item?.accountName == null || item?.accountName === '') ? 'N/A' : item?.accountName,
          accountNumber: (item?.accountNumber == null || item?.accountNumber === '') ? 'N/A' : item?.accountNumber,
          accountIsActive,
          // customers
          customerName: (item?.customerName == null || item?.customerName === '') ? 'N/A' : item?.customerName,
          customerId: (item?.customerId == null || item?.customerId === '') ? 'N/A' : item?.customerId,
          customerIsActive,
          // legal entities
          legalEntityClientIdentifier: (item?.legalEntityClientIdentifier == null || item?.legalEntityClientIdentifier === '') ? '' : item?.legalEntityClientIdentifier,
          legalEntityName: (item?.legalEntityName == null || item?.legalEntityName === '') ? 'N/A' : item?.legalEntityName,
          // forms
          formTypeDescription: item?.formTypeDescription ?? 'N/A',
          formType,
          validUntil: (item?.validUntil == null || item?.validUntil === '') ? 'N/A' : moment(item.validUntil).format('MMM DD, YYYY HH:mm:ss'),
          formTypeString: item?.formTypeString,
          workflowStage: item?.workflowStage,
          workflowPreviousUsername: (item?.workflowPreviousUsername === null || item?.workflowPreviousUsername === '') ? 'N/A' : item.workflowPreviousUsername,
          formSourceDescription: (item?.formSourceDescription == null || item?.formSourceDescription === '') ? 'N/A' : item?.formSourceDescription,
          workflowStageDescription: (item?.workflowStageDescription == null || item?.workflowStageDescription === '') ? 'N/A' : item?.workflowStageDescription,
          workflowStatus: (item?.workflowStatus == null || item?.workflowStatus === '') ? 'N/A' : item?.workflowStatus,
          validationDate: (item?.validationDate == null || item?.validationDate === '') ? 'N/A' : moment(item.validationDate).format('MMM DD, YYYY HH:mm:ss'),
          validityDescription: (item?.validityDescription == null || item?.validityDescription === '') ? 'N/A' : item?.validityDescription,
          // all
          additionalData: item.additionalData ?? null,
          isLoadingDetails: item?.isLoadingDetails,
        };

        data.push(datum);
      });
      const pageCount = Math.ceil(itemCount / gridCriteria.pageSize);

      this.setState({ data, gridCriteria: { ...gridCriteria, itemCount, pageCount, totalLimit: gridTotalLimit } });
    }
  };

  generateRequest = () => {
    const { searchCriteria, gridCriteria } = this.state;
    const formTypeArray = (searchCriteria.formType === '' || searchCriteria.formType == null) ? null : [searchCriteria.formType];
    let sortDirection;
    if (gridCriteria.sortBy != null && gridCriteria.sortBy[0]) {
      if (gridCriteria.sortBy[0]?.desc != null) {
        sortDirection = gridCriteria.sortBy[0]?.desc ? 'desc' : 'asc';
      }
    } else {
      sortDirection = null;
    }
    return ({
      pageNumber: gridCriteria.pageIndex + 1,
      pageSize: gridCriteria.pageSize,
      totalLimit: gridCriteria.totalLimit,
      sortByField: gridCriteria.sortBy != null ? gridCriteria.sortBy[0]?.id : null,
      sortDirection,
      filterList: gridCriteria.filters,
      clientIds: searchCriteria.clientIds,
      customerIdentifier: searchCriteria.customerIdentifier,
      customerName: searchCriteria.customerName,
      accountNumber: searchCriteria.accountNumber,
      accountName: searchCriteria.accountName,
      formTypes: formTypeArray
    });
  };

  onSearchCriteriaChanged = (newSearchCriteria) => {
    const { searchCriteria } = this.state;

    this.setState({ searchCriteria: { ...searchCriteria, ...newSearchCriteria } });
  };

  onSearchClicked = (newSearchCriteria) => {
    const { gridCriteria } = this.state;
    const { searchCriteria } = this.state;
    const { permissions } = this.props;
    const gridChanged = false;
    gridCriteria.pageIndex = 0;

    const criteria = { ...gridCriteria };
    criteria.columnDefs = advancedSearchColumnDefs(permissions).concat([]);

    this.setState({ loading: gridChanged, searchCriteria: { ...searchCriteria, ...newSearchCriteria }, gridCriteria: criteria }, (() => {
      this.fetchData();
    }));
  };

  onGridCriteriaChanged = (newGridCriteria) => {
    this.setState({ gridCriteria: { ...newGridCriteria } }, () => this.fetchData());
  };

  onRowSelectionChanged = (rowSelections) => {
    const { gridCriteria } = this.state;
    this.setState({ gridCriteria: { ...gridCriteria, ...rowSelections } });
  };

  sendDataRequest = () => {
    const { dispatch } = this.props;

    const request = this.generateRequest();
    dispatch(getAdvancedFormsByObject(request));
  };

  fetchData = () => {
    const { gridCriteria, loading } = this.state;

    if (loading) {
      return;
    }
    clearTimeout(this.timer);

    this.setState({ loading: true, gridCriteria: { ...gridCriteria, selectedItems: [] } }, () => {
      this.timer = setTimeout(() => {
        this.sendDataRequest();
      }, WAIT_INTERVAL);
    });
  };

  render() {
    const {
      data,
      searchCriteria,
      gridCriteria,
      loading,
      initialLoaded
    } = this.state;
    const {
      settings,
      isShowingClientCloneFormModal
    } = this.props;

    return (
      <div className="advanced-search landing-page">
        <AdvancedSearchPanel
          title="Advanced Search"
          searchCriteria={ searchCriteria }
          onSearchClicked={ this.onSearchClicked }
          onSearchCriteriaChanged={ this.onSearchCriteriaChanged }
          clientList={ settings?.clients || [] }
        />
        <div className="panel search-panel mt">
          <div className="panel-body results form-results">
            <ReactTable
              data={ data }
              gridCriteria={ gridCriteria }
              onGridCriteriaChanged={ this.onGridCriteriaChanged }
              onRowSelectionChanged={ this.onRowSelectionChanged }
              actions={ this.getActions() }
              loading={ loading }
              initialLoaded={ initialLoaded }
              className=""
            />
          </div>
        </div>
        { isShowingClientCloneFormModal && <CrossClientCloneModal
          isOpen={ isShowingClientCloneFormModal }
          onSubmit={ this.cloneForm }
          onClose={ this.closeCloneFormModal }
        /> }
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
      settings
    },
    advancedSearch: {
      searchCriteria,
      gridCriteria
    },
    forms: {
      forms,
      formsCount,
      gridTotalLimit,
      isUnassignModalShown,
      formsList,
      isShowingClientCloneFormModal,
      crossClientCloneForm
    },
  } = state;
  return {
    permissions,
    settings,
    forms,
    formsCount,
    gridTotalLimit,
    formsList,
    isUnassignModalShown,
    initSearchCriteria: searchCriteria,
    initGridCriteria: gridCriteria,
    isShowingClientCloneFormModal,
    crossClientCloneForm
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(AdvancedSearch, 'AdvancedSearch'));
